'use client';

import './video.scss';

import dynamic from 'next/dynamic';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoObject } from 'schema-dts';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { VideoProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { cookieConsentVideoURL } from '@sola/tracking/lib/video.ts';
import { getVideoType } from '@sola/utils/video.ts';

import { buildVideoJsonLd } from '../../utils/schema-utils.ts';
import { Image } from '../image/image.tsx';
import { JsonLd } from '../jsonLd/jsonLd.tsx';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

const VideoSkeleton = ({ ratio }: { ratio: string }) => (
  <div
    className="Video__skeleton"
    style={{ aspectRatio: ratio || '16/9', width: '100%', height: 'auto' }}
  >
    <Skeleton width="100%" height="100%" />
  </div>
);

export const Video = ({
  url,
  poster,
  isBackground,
  autoplay = false,
  allowPlaying = true,
  width,
  height,
  thumbnailUrl,
  title,
  uploadDate,
}: VideoProps) => {
  const { t } = useTranslation(['skippy']);
  const showImmediately = !isBackground && !autoplay;
  const [isLoading, setIsLoading] = useState(!showImmediately);
  const [playing, setPlaying] = useState(
    allowPlaying && (isBackground || autoplay),
  );

  const videoType = getVideoType(url);
  const variableVideoUrl = cookieConsentVideoURL(url);

  const handleVideoPauseButton = () => {
    if (playing) {
      trackClick(t('skippy:video.pause'));
      setPlaying(false);
    } else {
      trackClick(t('skippy:video.play'));
      setPlaying(true);
    }
  };

  useEffect(() => {
    if (!allowPlaying) {
      setPlaying(false);
    } else if (autoplay) {
      setPlaying(true);
    }
  }, [allowPlaying, autoplay]);

  const videoOptions = {
    default: {
      controls: true,
      config: {
        vimeo: { dnt: true },
      },
    },
    background: {
      loop: true,
      muted: true,
      autoPlay: true,
      controls: false,
      playsInline: true,
      config: {
        youtube: {
          modestbranding: 1 as const,
          showinfo: 0 as const,
          rel: 0 as const,
        },
        vimeo: {
          background: true,
          dnt: true,
          transparent: false,
          playsinline: true,
          muted: true,
        },
      },
    },
  };

  const shouldRenderSchema = uploadDate && (poster?.url || thumbnailUrl);
  const isPortrait = width && height && height > width;

  const getInlineWidth = () => (isPortrait ? 'auto' : '100%');

  return (
    <div
      className={cn(
        `Video Video--${videoType}`,
        isBackground ? 'Video--background' : 'Video--inline',
        isPortrait ? 'h-full' : 'w-full',
      )}
    >
      {shouldRenderSchema && (
        <JsonLd<VideoObject>
          item={{
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            ...buildVideoJsonLd({
              name: title || 'Video',
              thumbnailUrl: poster?.url || thumbnailUrl,
              contentUrl: variableVideoUrl,
              embedUrl: url,
              description: title,
              uploadDate,
            }),
          }}
        />
      )}
      {isBackground && poster && (
        <Image className="Video__poster" image={poster} priority />
      )}
      {!isBackground && isLoading && (
        <VideoSkeleton
          ratio={width && height ? `${width} / ${height}` : '16/9'}
        />
      )}
      <Suspense
        fallback={
          <VideoSkeleton
            ratio={width && height ? `${width} / ${height}` : '16/9'}
          />
        }
      >
        <ReactPlayer
          src={variableVideoUrl}
          playing={playing}
          wrapper="div"
          className={cn('Video__player', {
            'Video__player--portrait': isPortrait && width,
            'Video__player--landscape': !isPortrait && width,
            'Video__player--default': !width && !height,
          })}
          width={isBackground ? 'inherit' : getInlineWidth()}
          height={isBackground || isPortrait ? 'inherit' : 'auto'}
          onPlay={() => setPlaying(true)}
          onReady={() => setIsLoading(false)}
          onPlaying={() => setIsLoading(false)}
          {...videoOptions[isBackground ? 'background' : 'default']}
          style={{
            opacity: isBackground || !isLoading ? 1 : 0,
            transition: isBackground ? undefined : 'opacity 0.2s',
            aspectRatio:
              !isBackground && width && height
                ? `${width} / ${height}`
                : undefined,
          }}
        />
      </Suspense>
      {isBackground && (
        <div className="Video__controls absolute bottom-0 right-0 p-comfy-sm">
          <ButtonStyle variant="tertiary" size="md">
            <button type="button" onClick={handleVideoPauseButton}>
              {playing ? (
                <>
                  <ArkIcon iconName="pause-circle" size="20" />{' '}
                  {t('skippy:video.pause')}
                </>
              ) : (
                <>
                  <ArkIcon iconName="play-circle" size="20" />{' '}
                  {t('skippy:video.play')}
                </>
              )}
            </button>
          </ButtonStyle>
        </div>
      )}
    </div>
  );
};
